import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Variable helpers for template-form prompt + answer-rule fields.
 *
 * This module owns three concerns:
 *   1. VariableChips — a presentational chip row (letters as buttons).
 *   2. useTemplateVariableChips(form) — a hook that wires chip inserts
 *      into the form's prompt and answer-rule fields.
 *   3. PromptFieldFrame / AnswerFieldFrame — bordered containers that
 *      embed the input and a bottom-pinned toolbar inside the same
 *      frame, so chips + direction toggle sit INSIDE the field's
 *      visual box with a shared focus ring.
 *
 * Answer-field chips show ONLY the variables that currently appear in
 * the prompt (bare identifier insertion, no braces — the server
 * evaluator reads bare identifiers, see paramsFor() /
 * services/questions/generator.ts). When the prompt has no variables
 * yet, the answer toolbar shows a short muted hint instead.
 */
import React, { useMemo, useRef, useState } from 'react';
import { Button, Form, Input, Typography } from 'antd';
const { Text } = Typography;
const PLACEHOLDER_RE = /\{([a-zA-Z_][a-zA-Z_0-9]*)\}/g;
export function promptPlaceholders(prompt) {
    const out = new Set();
    for (const m of (prompt || '').matchAll(PLACEHOLDER_RE))
        out.add(m[1]);
    return out;
}
const DEFAULT_VARS = ['a', 'b', 'c', 'd'];
export function variableChipsFor(prompt) {
    const inPrompt = promptPlaceholders(prompt);
    const extras = [...inPrompt].filter((v) => !DEFAULT_VARS.includes(v)).sort();
    return [...DEFAULT_VARS, ...extras];
}
export function insertAtCursor(el, text, onInserted) {
    if (!el)
        return;
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    const next = el.value.slice(0, start) + text + el.value.slice(end);
    const caret = start + text.length;
    onInserted(next, caret);
    requestAnimationFrame(() => {
        el.focus();
        try {
            el.setSelectionRange(caret, caret);
        }
        catch { /* some types reject */ }
    });
}
// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------
export function useTemplateVariableChips(form, fields = { promptField: 'prompt', answerField: 'answer_rule' }) {
    const promptRef = useRef(null);
    const answerRef = useRef(null);
    const promptValue = Form.useWatch(fields.promptField, form) ?? '';
    // Prompt row: defaults + any extras already present. In-use chips are
    // highlighted so the author sees which letters already exist.
    const promptVars = useMemo(() => variableChipsFor(promptValue), [promptValue]);
    const inPrompt = useMemo(() => promptPlaceholders(promptValue), [promptValue]);
    // Answer row: show ONLY the variables used in the prompt. If the
    // prompt has none, the chip row is replaced with a short hint.
    const answerVars = useMemo(() => [...inPrompt].sort(), [inPrompt]);
    const insertIntoPrompt = (name) => {
        const el = promptRef.current?.resizableTextArea?.textArea ?? null;
        insertAtCursor(el, `{${name}}`, (next) => form.setFieldValue(fields.promptField, next));
    };
    const insertIntoAnswer = (name) => {
        const el = answerRef.current?.input ?? null;
        insertAtCursor(el, name, (next) => form.setFieldValue(fields.answerField, next));
    };
    const promptChips = (_jsx(VariableChips, { variables: promptVars, inUse: inPrompt, onInsert: insertIntoPrompt }));
    const answerChips = answerVars.length === 0
        ? _jsx(AnswerEmptyHint, {})
        : (_jsx(VariableChips, { variables: answerVars, onInsert: insertIntoAnswer }));
    return { promptRef, answerRef, promptChips, answerChips };
}
function AnswerEmptyHint() {
    return (_jsx(Text, { type: "secondary", style: { fontSize: 12 }, children: "\u05D4\u05DE\u05E9\u05EA\u05E0\u05D9\u05DD \u05D9\u05D5\u05E4\u05D9\u05E2\u05D5 \u05DB\u05D0\u05DF \u05D1\u05E8\u05D2\u05E2 \u05E9\u05EA\u05D5\u05E1\u05D9\u05E4\u05D5 \u05D0\u05D5\u05EA\u05DD \u05DC\u05E0\u05D5\u05E1\u05D7 \u05D4\u05E9\u05D0\u05DC\u05D4." }));
}
// ---------------------------------------------------------------------------
// Chip row
// ---------------------------------------------------------------------------
export function VariableChips({ variables, inUse, onInsert }) {
    return (_jsx("div", { style: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }, children: variables.map((name) => {
            const isInUse = !!inUse?.has(name);
            return (_jsx(Button, { size: "small", type: isInUse ? 'primary' : 'default', onClick: () => onInsert(name), style: {
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    minWidth: 32,
                    padding: '0 10px',
                    borderRadius: 999,
                    height: 24,
                }, children: name }, name));
        }) }));
}
// ---------------------------------------------------------------------------
// Framed field wrappers.
//
// Each wrapper draws a shared border + focus-within ring around a
// borderless input and a bottom-pinned toolbar. Form.Item injects
// value / onChange via React.cloneElement.
// ---------------------------------------------------------------------------
const FRAME_BASE = {
    borderRadius: 8,
    background: '#fff',
    border: '1px solid #d9d9d9',
    transition: 'border-color 160ms ease, box-shadow 160ms ease',
};
const FRAME_FOCUS = {
    borderColor: '#8B2FD6',
    boxShadow: '0 0 0 2px rgba(139, 47, 214, 0.12)',
};
const TOOLBAR = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    padding: '4px 8px',
    borderTop: '1px solid #f0f0f0',
};
export function PromptFieldFrame({ value, onChange, textAreaRef, dir, rows, chipsEl, toggleEl, }) {
    const [focused, setFocused] = useState(false);
    // Wrap the textarea in a `dir`-explicit box so the native <textarea>
    // inherits a definite direction regardless of the surrounding RTL
    // ConfigProvider. The TOOLBAR row sits outside this wrapper so its
    // own layout stays RTL (chips on the right, toggle on the left).
    return (_jsxs("div", { style: { ...FRAME_BASE, ...(focused ? FRAME_FOCUS : null), overflow: 'hidden' }, children: [_jsx("div", { dir: dir, style: { direction: dir }, children: _jsx(Input.TextArea, { ref: textAreaRef, value: value, onChange: onChange, variant: "borderless", onFocus: () => setFocused(true), onBlur: () => setFocused(false), dir: dir, rows: rows ?? 2, style: {
                        direction: dir,
                        textAlign: dir === 'rtl' ? 'right' : 'left',
                        boxShadow: 'none',
                        padding: '6px 11px',
                    } }) }), _jsxs("div", { style: TOOLBAR, children: [_jsx("div", { style: { minWidth: 0, overflow: 'hidden' }, children: chipsEl }), _jsx("div", { style: { flex: '0 0 auto' }, children: toggleEl })] })] }));
}
export function AnswerFieldFrame({ value, onChange, inputRef, chipsEl, placeholder, }) {
    const [focused, setFocused] = useState(false);
    // The native <input> is wrapped in a dir="ltr" box so it inherits
    // LTR direction even when the surrounding ConfigProvider is RTL.
    // Inline `direction: 'ltr'` on Input itself backs that up for cases
    // where antd forwards the prop to a wrapper span instead of the
    // native input. Toolbar stays in the outer RTL frame.
    return (_jsxs("div", { style: { ...FRAME_BASE, ...(focused ? FRAME_FOCUS : null), overflow: 'hidden' }, children: [_jsx("div", { dir: "ltr", style: { direction: 'ltr' }, children: _jsx(Input, { ref: inputRef, value: value, onChange: onChange, variant: "borderless", onFocus: () => setFocused(true), onBlur: () => setFocused(false), dir: "ltr", placeholder: placeholder, style: {
                        direction: 'ltr',
                        textAlign: 'left',
                        boxShadow: 'none',
                        padding: '6px 11px',
                    } }) }), _jsx("div", { style: TOOLBAR, children: _jsx("div", { style: { minWidth: 0, overflow: 'hidden' }, children: chipsEl }) })] }));
}
